import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useCancelableTask } from './useCancelableTask';
import {
  buildCumulativePointsFromCounts,
  buildDiscoverySeries,
  filterDiscoveryCounts,
  inferFirstDiscoveryYear,
} from '../series/discoverySeries';
import { bootstrapCurveCIFromCounts, bootstrapSolowSmithCI, combineBootstrapSamples } from '../models/bootstrap';
import { fitGompertz } from '../models/gompertz';
import { fitLogistic } from '../models/logistic';
import { fitSolowSmithCore } from '../models/solowSmith';
import { createBootstrapWorkerClient } from '../utils/bootstrapWorkerClient';

export const useHighResFit = ({
  groupStats,
  filteredRecords,
  startYear,
  endYear,
  unitType,
  highResSettings,
  randomSeed,
  curveFitMethod,
  getRandomFn,
  t,
}) => {
  const task = useCancelableTask();
  const workerRef = useRef(null);

  const {
    minGroupEvents,
    minActiveYears,
    minSpanYears,
    poolLowQuality,
    solowGuardEnabled,
    solowMaxRatio,
    solowMinR2,
    curveGuardEnabled,
    curveMinR2,
    highResComputeCI,
    highResBootstrapSamples,
  } = highResSettings;

  const eligibility = useMemo(() => {
    let eligible = 0;
    let pooled = 0;
    groupStats.forEach((stat) => {
      const meetsThreshold = stat.eventCount >= minGroupEvents
        && stat.nonZeroYears >= minActiveYears
        && stat.spanYears >= minSpanYears
        && stat.modelFitPoints.length >= 5;
      if (meetsThreshold) {
        eligible += 1;
      } else {
        pooled += 1;
      }
    });
    return { eligible, pooled };
  }, [groupStats, minGroupEvents, minActiveYears, minSpanYears]);

  const groupCount = groupStats.length;

  const disabledReason = useMemo(() => {
    if (unitType === 'subfamily') return t('estimationPage.highResDisabledSubfamily');
    if (filteredRecords.length === 0) return t('noData');
    if (groupCount < 2) return t('estimationPage.highResDisabledSingleGroup');
    if (eligibility.eligible === 0 && (!poolLowQuality || eligibility.pooled === 0)) {
      return t('estimationPage.highResDisabledNoEligible');
    }
    return '';
  }, [unitType, filteredRecords.length, groupCount, eligibility, poolLowQuality, t]);

  const canRun = !disabledReason;

  useEffect(() => {
    task.cancel();
    task.reset();
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, [
    task.cancel,
    task.reset,
    groupStats,
    filteredRecords.length,
    startYear,
    endYear,
    minGroupEvents,
    minActiveYears,
    minSpanYears,
    poolLowQuality,
    solowGuardEnabled,
    solowMaxRatio,
    solowMinR2,
    curveGuardEnabled,
    curveMinR2,
    highResComputeCI,
    highResBootstrapSamples,
    randomSeed,
  ]);

  const run = useCallback(() => {
    if (!canRun || task.loading) return;

    task.run(async ({ updateStage, updateProgress, setResult, setError, yieldToMain, isActive }) => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
      const workerClient = createBootstrapWorkerClient();
      workerRef.current = workerClient;

      try {
        updateStage(t('estimationPage.highResPreparing'), 5);
        await yieldToMain();

        const randomFn = getRandomFn();
        const yieldEvery = 10;

        const eligibleGroups = [];
        const pooledRecordIndexes = [];
        const pooledSolowIndexes = [];
        const pooledLogisticIndexes = [];
        const pooledGompertzIndexes = [];
        const solowGuard = { excluded: 0, pooled: 0 };
        const curveGuard = {
          logisticExcluded: 0,
          logisticPooled: 0,
          gompertzExcluded: 0,
          gompertzPooled: 0,
        };
        const curveGuardActive = curveGuardEnabled && Number.isFinite(curveMinR2) && curveMinR2 > 0;
        const shouldComputeCI = highResComputeCI;
        const mapIndexesToRecords = (indexes) => (
          indexes.map(index => filteredRecords[index]).filter(Boolean)
        );
        const mergeIndexes = (...lists) => {
          const set = new Set();
          lists.forEach((list) => {
            list.forEach((index) => set.add(index));
          });
          return Array.from(set);
        };

        groupStats.forEach((stat) => {
          const meetsThreshold = stat.eventCount >= minGroupEvents
            && stat.nonZeroYears >= minActiveYears
            && stat.spanYears >= minSpanYears
            && stat.modelFitPoints.length >= 5;
          if (meetsThreshold) {
            eligibleGroups.push(stat);
          } else {
            pooledRecordIndexes.push(...stat.recordIndexes);
          }
        });

        const totalGroups = groupStats.length;
        const pooledGroupCount = Math.max(0, totalGroups - eligibleGroups.length);
        const totalSteps = eligibleGroups.length + (poolLowQuality ? 1 : 0);
        if (totalGroups === 0 || (eligibleGroups.length === 0 && (!poolLowQuality || pooledRecordIndexes.length === 0))) {
          setError(t('estimationPage.highResNoEligible'));
          return;
        }

        const aggregate = {
          solowSmith: {
            total: 0,
            observed: 0,
            groupsUsed: 0,
            groupsPooled: poolLowQuality ? pooledGroupCount : 0,
            pooledIncluded: false,
            guardExcluded: 0,
            guardPooled: 0,
            ciComplete: shouldComputeCI,
            sampleSets: [],
            fixedTotal: 0,
          },
          logistic: {
            total: 0,
            observed: 0,
            groupsUsed: 0,
            groupsPooled: poolLowQuality ? pooledGroupCount : 0,
            pooledIncluded: false,
            guardExcluded: 0,
            guardPooled: 0,
            ciComplete: shouldComputeCI,
            sampleSets: [],
            fixedTotal: 0,
          },
          gompertz: {
            total: 0,
            observed: 0,
            groupsUsed: 0,
            groupsPooled: poolLowQuality ? pooledGroupCount : 0,
            pooledIncluded: false,
            guardExcluded: 0,
            guardPooled: 0,
            ciComplete: shouldComputeCI,
            sampleSets: [],
            fixedTotal: 0,
          },
        };

      for (let i = 0; i < eligibleGroups.length; i += 1) {
        if (!isActive()) return;
        const groupStat = eligibleGroups[i];
        const progress = 10 + ((i + 1) / totalSteps) * 80;
        updateStage(
          t('estimationPage.highResProgress', { current: i + 1, total: totalSteps }),
          progress
        );
        await yieldToMain();

        const series = groupStat.series;
        const currentCount = series.currentCount;
        const modelStart = groupStat.modelStartYear;
        const modelDiscovery = groupStat.modelDiscoveryCounts;
        const modelFitPointsLocal = groupStat.modelFitPoints;
        const solowObserved = modelFitPointsLocal.map(point => ({ x: point.year, y: point.y }));
        const solow = fitSolowSmithCore(modelDiscovery, modelStart, endYear, solowObserved);
        let solowAccepted = false;
        if (solow && Number.isFinite(solow.total) && Number.isFinite(currentCount) && currentCount > 0) {
          const ratio = solow.total / currentCount;
          const ratioOk = !solowGuardEnabled
            || !Number.isFinite(solowMaxRatio)
            || solowMaxRatio <= 0
            || (Number.isFinite(ratio) && ratio <= solowMaxRatio);
          const r2Ok = !solowGuardEnabled
            || !Number.isFinite(solowMinR2)
            || solowMinR2 <= 0
            || (Number.isFinite(solow.rSquared) && solow.rSquared >= solowMinR2);
          solowAccepted = ratioOk && r2Ok;
        }

        if (solowAccepted) {
          aggregate.solowSmith.total += solow.total;
          aggregate.solowSmith.observed += currentCount;
          aggregate.solowSmith.groupsUsed += 1;
          if (shouldComputeCI) {
            const ci = workerClient
              ? await workerClient.runJob('BOOTSTRAP_SOLOW_CI', {
                  baseFit: solow,
                  startYear: modelStart,
                  endYear,
                  iterations: highResBootstrapSamples,
                  varyN: true,
                  includeCurve: false,
                  randomSeed,
                  sharedRandom: Boolean(randomSeed),
                })
              : await bootstrapSolowSmithCI(
                  solow,
                  modelStart,
                  endYear,
                  highResBootstrapSamples,
                  {
                    varyN: true,
                    randomFn,
                    includeCurve: false,
                    yieldEvery,
                    yieldFn: yieldToMain,
                  }
                );
            if (ci && Array.isArray(ci.values) && ci.values.length > 0) {
              aggregate.solowSmith.sampleSets.push(ci.values);
            } else {
              aggregate.solowSmith.fixedTotal += solow.total;
              aggregate.solowSmith.ciComplete = false;
            }
          }
        } else if (solow && solowGuardEnabled) {
          solowGuard.excluded += 1;
          if (poolLowQuality) {
            pooledSolowIndexes.push(...groupStat.recordIndexes);
            solowGuard.pooled += 1;
          }
        }

        const logistic = fitLogistic(modelFitPointsLocal, {
          discoveryCounts: modelDiscovery,
          modelStartYear: modelStart,
          fitMethod: curveFitMethod,
        });
        let logisticAccepted = false;
        if (logistic && logistic.params && Number.isFinite(logistic.params.L)) {
          const r2Ok = !curveGuardActive
            || (Number.isFinite(logistic.rSquared) && logistic.rSquared >= curveMinR2);
          logisticAccepted = r2Ok;
        }

        if (logisticAccepted) {
          aggregate.logistic.total += logistic.params.L;
          aggregate.logistic.observed += currentCount;
          aggregate.logistic.groupsUsed += 1;
          if (shouldComputeCI) {
            const ci = workerClient
              ? await workerClient.runJob('BOOTSTRAP_CURVE', {
                  model: 'logistic',
                  discoveryCounts: modelDiscovery,
                  modelStartYear: modelStart,
                  iterations: highResBootstrapSamples,
                  includeCurve: false,
                  fitMethod: curveFitMethod,
                  randomSeed,
                  sharedRandom: Boolean(randomSeed),
                })
              : await bootstrapCurveCIFromCounts(
                  modelDiscovery,
                  modelStart,
                  fitLogistic,
                  highResBootstrapSamples,
                  params => params.L,
                  {
                    randomFn,
                    includeCurve: false,
                    fitMethod: curveFitMethod,
                    yieldEvery,
                    yieldFn: yieldToMain,
                  }
                );
            if (ci && Array.isArray(ci.values) && ci.values.length > 0) {
              aggregate.logistic.sampleSets.push(ci.values);
            } else {
              aggregate.logistic.fixedTotal += logistic.params.L;
              aggregate.logistic.ciComplete = false;
            }
          }
        } else if (curveGuardActive && logistic && logistic.params && Number.isFinite(logistic.params.L)) {
          curveGuard.logisticExcluded += 1;
          if (poolLowQuality) {
            pooledLogisticIndexes.push(...groupStat.recordIndexes);
            curveGuard.logisticPooled += 1;
          }
        }

        const gompertz = fitGompertz(modelFitPointsLocal, {
          discoveryCounts: modelDiscovery,
          modelStartYear: modelStart,
          fitMethod: curveFitMethod,
        });
        let gompertzAccepted = false;
        if (gompertz && gompertz.params && Number.isFinite(gompertz.params.a)) {
          const r2Ok = !curveGuardActive
            || (Number.isFinite(gompertz.rSquared) && gompertz.rSquared >= curveMinR2);
          gompertzAccepted = r2Ok;
        }

        if (gompertzAccepted) {
          aggregate.gompertz.total += gompertz.params.a;
          aggregate.gompertz.observed += currentCount;
          aggregate.gompertz.groupsUsed += 1;
          if (shouldComputeCI) {
            const ci = workerClient
              ? await workerClient.runJob('BOOTSTRAP_CURVE', {
                  model: 'gompertz',
                  discoveryCounts: modelDiscovery,
                  modelStartYear: modelStart,
                  iterations: highResBootstrapSamples,
                  includeCurve: false,
                  fitMethod: curveFitMethod,
                  randomSeed,
                  sharedRandom: Boolean(randomSeed),
                })
              : await bootstrapCurveCIFromCounts(
                  modelDiscovery,
                  modelStart,
                  fitGompertz,
                  highResBootstrapSamples,
                  params => params.a,
                  {
                    randomFn,
                    includeCurve: false,
                    fitMethod: curveFitMethod,
                    yieldEvery,
                    yieldFn: yieldToMain,
                  }
                );
            if (ci && Array.isArray(ci.values) && ci.values.length > 0) {
              aggregate.gompertz.sampleSets.push(ci.values);
            } else {
              aggregate.gompertz.fixedTotal += gompertz.params.a;
              aggregate.gompertz.ciComplete = false;
            }
          }
        } else if (curveGuardActive && gompertz && gompertz.params && Number.isFinite(gompertz.params.a)) {
          curveGuard.gompertzExcluded += 1;
          if (poolLowQuality) {
            pooledGompertzIndexes.push(...groupStat.recordIndexes);
            curveGuard.gompertzPooled += 1;
          }
        }
      }

      aggregate.solowSmith.guardExcluded = solowGuard.excluded;
      aggregate.solowSmith.guardPooled = solowGuard.pooled;
      aggregate.logistic.guardExcluded = curveGuard.logisticExcluded;
      aggregate.logistic.guardPooled = curveGuard.logisticPooled;
      aggregate.gompertz.guardExcluded = curveGuard.gompertzExcluded;
      aggregate.gompertz.guardPooled = curveGuard.gompertzPooled;
      aggregate.solowSmith.groupsPooled = poolLowQuality ? pooledGroupCount + solowGuard.pooled : 0;
      aggregate.logistic.groupsPooled = poolLowQuality ? pooledGroupCount + curveGuard.logisticPooled : 0;
      aggregate.gompertz.groupsPooled = poolLowQuality ? pooledGroupCount + curveGuard.gompertzPooled : 0;

      const pooledSolowRecordsAll = poolLowQuality
        ? mapIndexesToRecords(mergeIndexes(pooledRecordIndexes, pooledSolowIndexes))
        : [];
      const pooledLogisticRecordsAll = poolLowQuality
        ? mapIndexesToRecords(mergeIndexes(pooledRecordIndexes, pooledLogisticIndexes))
        : [];
      const pooledGompertzRecordsAll = poolLowQuality
        ? mapIndexesToRecords(mergeIndexes(pooledRecordIndexes, pooledGompertzIndexes))
        : [];
      const shouldPool = poolLowQuality && (
        pooledSolowRecordsAll.length > 0
        || pooledLogisticRecordsAll.length > 0
        || pooledGompertzRecordsAll.length > 0
      );

      if (shouldPool) {
        updateStage(t('estimationPage.highResPooling'), 10 + ((eligibleGroups.length + 1) / totalSteps) * 80);
        await yieldToMain();

        const pooledSolowSeries = pooledSolowRecordsAll.length > 0
          ? buildDiscoverySeries(pooledSolowRecordsAll, startYear, endYear)
          : null;
        if (pooledSolowSeries) {
          const pooledSolowStart = inferFirstDiscoveryYear(pooledSolowSeries.discoveryCounts) ?? startYear;
          const pooledSolowCounts = filterDiscoveryCounts(pooledSolowSeries.discoveryCounts, pooledSolowStart);
          const pooledSolowFitPoints = buildCumulativePointsFromCounts(pooledSolowCounts, pooledSolowStart);
          if (pooledSolowFitPoints.length >= 5) {
            const pooledSolowObserved = pooledSolowFitPoints.map(point => ({ x: point.year, y: point.y }));
              const solow = fitSolowSmithCore(
                pooledSolowCounts,
                pooledSolowStart,
                endYear,
                pooledSolowObserved
              );
            let pooledAccepted = false;
            if (solow && Number.isFinite(solow.total) && pooledSolowSeries.currentCount > 0) {
              const ratio = solow.total / pooledSolowSeries.currentCount;
              const ratioOk = !solowGuardEnabled
                || !Number.isFinite(solowMaxRatio)
                || solowMaxRatio <= 0
                || (Number.isFinite(ratio) && ratio <= solowMaxRatio);
              const r2Ok = !solowGuardEnabled
                || !Number.isFinite(solowMinR2)
                || solowMinR2 <= 0
                || (Number.isFinite(solow.rSquared) && solow.rSquared >= solowMinR2);
              pooledAccepted = ratioOk && r2Ok;
            }
            if (pooledAccepted) {
              aggregate.solowSmith.total += solow.total;
              aggregate.solowSmith.observed += pooledSolowSeries.currentCount;
              aggregate.solowSmith.pooledIncluded = true;
                if (shouldComputeCI) {
                  const ci = workerClient
                    ? await workerClient.runJob('BOOTSTRAP_SOLOW_CI', {
                        baseFit: solow,
                        startYear: pooledSolowStart,
                        endYear,
                        iterations: highResBootstrapSamples,
                        varyN: true,
                        includeCurve: false,
                        randomSeed,
                        sharedRandom: Boolean(randomSeed),
                      })
                    : await bootstrapSolowSmithCI(
                        solow,
                        pooledSolowStart,
                        endYear,
                        highResBootstrapSamples,
                        {
                          varyN: true,
                          randomFn,
                          includeCurve: false,
                          yieldEvery,
                          yieldFn: yieldToMain,
                        }
                      );
                  if (ci && Array.isArray(ci.values) && ci.values.length > 0) {
                    aggregate.solowSmith.sampleSets.push(ci.values);
                  } else {
                    aggregate.solowSmith.fixedTotal += solow.total;
                    aggregate.solowSmith.ciComplete = false;
                  }
                }
              }
            }
          }

        const pooledLogisticSeries = pooledLogisticRecordsAll.length > 0
          ? buildDiscoverySeries(pooledLogisticRecordsAll, startYear, endYear)
          : null;
        if (pooledLogisticSeries) {
          const pooledLogisticStart = inferFirstDiscoveryYear(pooledLogisticSeries.discoveryCounts) ?? startYear;
          const pooledLogisticCounts = filterDiscoveryCounts(pooledLogisticSeries.discoveryCounts, pooledLogisticStart);
          const pooledLogisticFitPoints = buildCumulativePointsFromCounts(pooledLogisticCounts, pooledLogisticStart);
          if (pooledLogisticFitPoints.length >= 5) {
              const logistic = fitLogistic(pooledLogisticFitPoints, {
                discoveryCounts: pooledLogisticCounts,
                modelStartYear: pooledLogisticStart,
                fitMethod: curveFitMethod,
              });
            let pooledAccepted = false;
            if (logistic && logistic.params && Number.isFinite(logistic.params.L)) {
              const r2Ok = !curveGuardActive
                || (Number.isFinite(logistic.rSquared) && logistic.rSquared >= curveMinR2);
              pooledAccepted = r2Ok;
            }
            if (pooledAccepted) {
              aggregate.logistic.total += logistic.params.L;
              aggregate.logistic.observed += pooledLogisticSeries.currentCount;
              aggregate.logistic.pooledIncluded = true;
                if (shouldComputeCI) {
                  const ci = workerClient
                    ? await workerClient.runJob('BOOTSTRAP_CURVE', {
                        model: 'logistic',
                        discoveryCounts: pooledLogisticCounts,
                        modelStartYear: pooledLogisticStart,
                        iterations: highResBootstrapSamples,
                        includeCurve: false,
                        fitMethod: curveFitMethod,
                        randomSeed,
                        sharedRandom: Boolean(randomSeed),
                      })
                    : await bootstrapCurveCIFromCounts(
                        pooledLogisticCounts,
                        pooledLogisticStart,
                        fitLogistic,
                        highResBootstrapSamples,
                        params => params.L,
                        {
                          randomFn,
                          includeCurve: false,
                          fitMethod: curveFitMethod,
                          yieldEvery,
                          yieldFn: yieldToMain,
                        }
                      );
                  if (ci && Array.isArray(ci.values) && ci.values.length > 0) {
                    aggregate.logistic.sampleSets.push(ci.values);
                  } else {
                    aggregate.logistic.fixedTotal += logistic.params.L;
                    aggregate.logistic.ciComplete = false;
                  }
                }
              }
            }
          }

        const pooledGompertzSeries = pooledGompertzRecordsAll.length > 0
          ? buildDiscoverySeries(pooledGompertzRecordsAll, startYear, endYear)
          : null;
        if (pooledGompertzSeries) {
          const pooledGompertzStart = inferFirstDiscoveryYear(pooledGompertzSeries.discoveryCounts) ?? startYear;
          const pooledGompertzCounts = filterDiscoveryCounts(pooledGompertzSeries.discoveryCounts, pooledGompertzStart);
          const pooledGompertzFitPoints = buildCumulativePointsFromCounts(pooledGompertzCounts, pooledGompertzStart);
          if (pooledGompertzFitPoints.length >= 5) {
              const gompertz = fitGompertz(pooledGompertzFitPoints, {
                discoveryCounts: pooledGompertzCounts,
                modelStartYear: pooledGompertzStart,
                fitMethod: curveFitMethod,
              });
            let pooledAccepted = false;
            if (gompertz && gompertz.params && Number.isFinite(gompertz.params.a)) {
              const r2Ok = !curveGuardActive
                || (Number.isFinite(gompertz.rSquared) && gompertz.rSquared >= curveMinR2);
              pooledAccepted = r2Ok;
            }
            if (pooledAccepted) {
              aggregate.gompertz.total += gompertz.params.a;
              aggregate.gompertz.observed += pooledGompertzSeries.currentCount;
              aggregate.gompertz.pooledIncluded = true;
                if (shouldComputeCI) {
                  const ci = workerClient
                    ? await workerClient.runJob('BOOTSTRAP_CURVE', {
                        model: 'gompertz',
                        discoveryCounts: pooledGompertzCounts,
                        modelStartYear: pooledGompertzStart,
                        iterations: highResBootstrapSamples,
                        includeCurve: false,
                        fitMethod: curveFitMethod,
                        randomSeed,
                        sharedRandom: Boolean(randomSeed),
                      })
                    : await bootstrapCurveCIFromCounts(
                        pooledGompertzCounts,
                        pooledGompertzStart,
                        fitGompertz,
                        highResBootstrapSamples,
                        params => params.a,
                        {
                          randomFn,
                          includeCurve: false,
                          fitMethod: curveFitMethod,
                          yieldEvery,
                          yieldFn: yieldToMain,
                        }
                      );
                  if (ci && Array.isArray(ci.values) && ci.values.length > 0) {
                    aggregate.gompertz.sampleSets.push(ci.values);
                  } else {
                    aggregate.gompertz.fixedTotal += gompertz.params.a;
                    aggregate.gompertz.ciComplete = false;
                  }
                }
              }
            }
          }
        }

      updateStage(t('estimationPage.highResFinalize'), 95);
      await yieldToMain();

      if (!isActive()) return;

      const solowCombinedCI = shouldComputeCI
        ? combineBootstrapSamples(
            aggregate.solowSmith.sampleSets,
            highResBootstrapSamples,
            randomFn,
            aggregate.solowSmith.fixedTotal
          )
        : null;
      if (shouldComputeCI && !solowCombinedCI) {
        aggregate.solowSmith.ciComplete = false;
      }
      const solowCI = shouldComputeCI && !solowCombinedCI && Number.isFinite(aggregate.solowSmith.total)
        ? { lower: aggregate.solowSmith.total, upper: aggregate.solowSmith.total }
        : solowCombinedCI;

      const logisticCombinedCI = shouldComputeCI
        ? combineBootstrapSamples(
            aggregate.logistic.sampleSets,
            highResBootstrapSamples,
            randomFn,
            aggregate.logistic.fixedTotal
          )
        : null;
      if (shouldComputeCI && !logisticCombinedCI) {
        aggregate.logistic.ciComplete = false;
      }
      const logisticCI = shouldComputeCI && !logisticCombinedCI && Number.isFinite(aggregate.logistic.total)
        ? { lower: aggregate.logistic.total, upper: aggregate.logistic.total }
        : logisticCombinedCI;

      const gompertzCombinedCI = shouldComputeCI
        ? combineBootstrapSamples(
            aggregate.gompertz.sampleSets,
            highResBootstrapSamples,
            randomFn,
            aggregate.gompertz.fixedTotal
          )
        : null;
      if (shouldComputeCI && !gompertzCombinedCI) {
        aggregate.gompertz.ciComplete = false;
      }
      const gompertzCI = shouldComputeCI && !gompertzCombinedCI && Number.isFinite(aggregate.gompertz.total)
        ? { lower: aggregate.gompertz.total, upper: aggregate.gompertz.total }
        : gompertzCombinedCI;

      const solowSmith = (aggregate.solowSmith.groupsUsed > 0 || aggregate.solowSmith.pooledIncluded)
        ? {
            total: aggregate.solowSmith.total,
            observed: aggregate.solowSmith.observed,
            undiscovered: aggregate.solowSmith.total - aggregate.solowSmith.observed,
            ci: shouldComputeCI && solowCI ? solowCI : null,
            ciComplete: shouldComputeCI ? aggregate.solowSmith.ciComplete : false,
            groupsUsed: aggregate.solowSmith.groupsUsed,
            groupsTotal: totalGroups,
            groupsPooled: aggregate.solowSmith.groupsPooled,
            pooledIncluded: aggregate.solowSmith.pooledIncluded,
            guardExcluded: aggregate.solowSmith.guardExcluded,
            guardPooled: aggregate.solowSmith.guardPooled,
          }
        : null;

      const logistic = (aggregate.logistic.groupsUsed > 0 || aggregate.logistic.pooledIncluded)
        ? {
            total: aggregate.logistic.total,
            observed: aggregate.logistic.observed,
            undiscovered: aggregate.logistic.total - aggregate.logistic.observed,
            ci: shouldComputeCI && logisticCI ? logisticCI : null,
            ciComplete: shouldComputeCI ? aggregate.logistic.ciComplete : false,
            groupsUsed: aggregate.logistic.groupsUsed,
            groupsTotal: totalGroups,
            groupsPooled: aggregate.logistic.groupsPooled,
            pooledIncluded: aggregate.logistic.pooledIncluded,
            guardExcluded: aggregate.logistic.guardExcluded,
            guardPooled: aggregate.logistic.guardPooled,
          }
        : null;

      const gompertz = (aggregate.gompertz.groupsUsed > 0 || aggregate.gompertz.pooledIncluded)
        ? {
            total: aggregate.gompertz.total,
            observed: aggregate.gompertz.observed,
            undiscovered: aggregate.gompertz.total - aggregate.gompertz.observed,
            ci: shouldComputeCI && gompertzCI ? gompertzCI : null,
            ciComplete: shouldComputeCI ? aggregate.gompertz.ciComplete : false,
            groupsUsed: aggregate.gompertz.groupsUsed,
            groupsTotal: totalGroups,
            groupsPooled: aggregate.gompertz.groupsPooled,
            pooledIncluded: aggregate.gompertz.pooledIncluded,
            guardExcluded: aggregate.gompertz.guardExcluded,
            guardPooled: aggregate.gompertz.guardPooled,
          }
        : null;

        setResult({ solowSmith, logistic, gompertz, groupsTotal: totalGroups });
        updateProgress(100);

        if (!solowSmith && !logistic && !gompertz) {
          setError(t('estimationPage.fitFailed'));
        }
      } catch (error) {
        if (isActive()) {
          setError(t('estimationPage.fitFailed'));
        }
      } finally {
        if (workerRef.current) {
          workerRef.current.terminate();
          workerRef.current = null;
        }
      }
    });
  }, [
    canRun,
    task.loading,
    task.run,
    groupStats,
    filteredRecords,
    startYear,
    endYear,
    minGroupEvents,
    minActiveYears,
    minSpanYears,
    poolLowQuality,
    solowGuardEnabled,
    solowMaxRatio,
    solowMinR2,
    curveGuardEnabled,
    curveMinR2,
    highResComputeCI,
    highResBootstrapSamples,
    curveFitMethod,
    randomSeed,
    getRandomFn,
    t,
  ]);

  return {
    ...task,
    results: task.result,
    eligibility,
    groupCount,
    disabledReason,
    canRun,
    run,
  };
};
